// Geometry helpers shared by the Worker, the Durable Object and the browser.

export const EARTH_RADIUS_KM = 6371;
/** Light in optical fiber travels at roughly c / 1.468 ≈ 204,000 km/s. */
export const FIBER_KM_PER_MS = 204;
/** Real cable routes are never great circles; this is a commonly used stretch factor. */
export const ROUTE_STRETCH = 1.35;

export interface LatLon {
  lat: number;
  lon: number;
}

export type Vec3 = [number, number, number];

const DEG = Math.PI / 180;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Wraps a longitude into [-180, 180). */
export function wrapLon(lon: number): number {
  const w = ((((lon + 180) % 360) + 360) % 360) - 180;
  return w;
}

/**
 * Unit vector for a lat/lon. Convention: +Y is the north pole, the prime meridian
 * points at +Z, and +X points at 90°E. The globe renderer uses the same convention.
 */
export function latLonToVec3(lat: number, lon: number, r = 1): Vec3 {
  const phi = lat * DEG;
  const lambda = lon * DEG;
  const c = Math.cos(phi);
  return [r * c * Math.sin(lambda), r * Math.sin(phi), r * c * Math.cos(lambda)];
}

export function vec3ToLatLon(v: Vec3): LatLon {
  const [x, y, z] = v;
  const r = Math.hypot(x, y, z) || 1;
  return { lat: Math.asin(clamp(y / r, -1, 1)) / DEG, lon: Math.atan2(x, z) / DEG };
}

/** Great-circle distance in kilometres (haversine). */
export function distanceKm(a: LatLon, b: LatLon): number {
  const dLat = (b.lat - a.lat) * DEG;
  const dLon = (b.lon - a.lon) * DEG;
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * DEG) * Math.cos(b.lat * DEG) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** One-way propagation delay through fiber between two points, in milliseconds. */
export function fiberLatencyMs(a: LatLon, b: LatLon): number {
  return (distanceKm(a, b) * ROUTE_STRETCH) / FIBER_KM_PER_MS;
}

/** Spherical linear interpolation between two unit vectors. */
export function slerp(a: Vec3, b: Vec3, t: number): Vec3 {
  let dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  dot = clamp(dot, -1, 1);
  const omega = Math.acos(dot);
  if (omega < 1e-6) {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
  }
  const so = Math.sin(omega);
  const wa = Math.sin((1 - t) * omega) / so;
  const wb = Math.sin(t * omega) / so;
  return [a[0] * wa + b[0] * wb, a[1] * wa + b[1] * wb, a[2] * wa + b[2] * wb];
}

/**
 * Samples a great-circle arc lifted off the sphere. The lift follows a sine bump
 * whose height scales with the angular distance, so short hops stay low and
 * intercontinental hops arc high over the planet.
 */
export function arcPoints(a: LatLon, b: LatLon, segments: number, radius = 1): Vec3[] {
  const va = latLonToVec3(a.lat, a.lon);
  const vb = latLonToVec3(b.lat, b.lon);
  const angle = Math.acos(clamp(va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2], -1, 1));
  const lift = 0.015 + angle * 0.18;
  const pts: Vec3[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const p = slerp(va, vb, t);
    const h = radius * (1 + lift * Math.sin(Math.PI * t));
    const len = Math.hypot(p[0], p[1], p[2]) || 1;
    pts.push([(p[0] / len) * h, (p[1] / len) * h, (p[2] / len) * h]);
  }
  return pts;
}

/** Deterministic small offset so that city-level geolocation is not shown exactly. */
export function fuzzLocation(p: LatLon, seed: number, maxKm = 18): LatLon {
  const a = ((seed >>> 0) % 3600) / 10;
  const d = (((seed >>> 12) % 1000) / 1000) * maxKm;
  const dLat = (d / 111) * Math.cos(a * DEG);
  const dLon = (d / (111 * Math.max(0.2, Math.cos(p.lat * DEG)))) * Math.sin(a * DEG);
  return {
    lat: Math.round(clamp(p.lat + dLat, -89.9, 89.9) * 100) / 100,
    lon: Math.round(wrapLon(p.lon + dLon) * 100) / 100,
  };
}

/** Day of year (1-based) in UTC. */
export function dayOfYear(ms: number): number {
  const d = new Date(ms);
  const start = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.floor((ms - start) / 86_400_000) + 1;
}

/**
 * Approximate subsolar point: the place on Earth where the sun is directly overhead.
 * Uses the NOAA equation-of-time approximation; accurate to well under a degree,
 * which is plenty for lighting a globe and picking a musical key.
 */
export function subsolarPoint(ms: number): LatLon {
  const doy = dayOfYear(ms);
  const d = new Date(ms);
  const hours = d.getUTCHours() + d.getUTCMinutes() / 60 + d.getUTCSeconds() / 3600;
  const gamma = ((2 * Math.PI) / 365) * (doy - 1 + (hours - 12) / 24);
  const eqTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(gamma) -
      0.032077 * Math.sin(gamma) -
      0.014615 * Math.cos(2 * gamma) -
      0.040849 * Math.sin(2 * gamma));
  const decl =
    0.006918 -
    0.399912 * Math.cos(gamma) +
    0.070257 * Math.sin(gamma) -
    0.006758 * Math.cos(2 * gamma) +
    0.000907 * Math.sin(2 * gamma) -
    0.002697 * Math.cos(3 * gamma) +
    0.00148 * Math.sin(3 * gamma);
  const minutes = hours * 60 + eqTime;
  const lon = wrapLon(-(minutes - 720) / 4);
  return { lat: decl / DEG, lon };
}

/** Solar elevation angle in degrees for a point on Earth at a given instant. */
export function solarElevation(p: LatLon, ms: number): number {
  const s = subsolarPoint(ms);
  const a = latLonToVec3(p.lat, p.lon);
  const b = latLonToVec3(s.lat, s.lon);
  const dot = clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1);
  return 90 - Math.acos(dot) / DEG;
}
